import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ImagePlus } from "lucide-react";
import showAlert from "../../server/ShowAlert";
import { getPresignedUrl } from "../../server/Filter";
import { UpdateProduct } from "../../server/Product";
import { getCropperImageUrl } from "../../utils/productImageLoader";
import {
  ORIGINAL_IMAGE_SOURCE,
  resolveCroppedImageSource,
} from "../../utils/productImageSource";
import MyCropperComp from "./MyCrooperComp";

const getUploadErrorMessage = (err) => {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const apiMessage =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.response?.data;

    if (status === 403) {
      return "אין הרשאה לעדכן תמונות. פנה למנהל המערכת.";
    }
    if (typeof apiMessage === "string" && apiMessage.trim()) {
      return apiMessage;
    }
    if (status) return `שגיאת שרת (${status})`;
  }

  return "העלאת התמונה נכשלה";
};

const MyCropper = ({
  itemId,
  uploadImg,
  usePgwProductImage = false,
  recrop = null,
  onRecropClose,
}) => {
  const [src, setSrc] = useState(null);
  const [srcPrev, setSrcPrev] = useState(null);
  const [cropped, setCropped] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preloading, setPreloading] = useState(false);
  const [sourceMedia, setSourceMedia] = useState(null);
  const sourceMediaRef = useRef(sourceMedia);

  useEffect(() => {
    sourceMediaRef.current = sourceMedia;
  }, [sourceMedia]);

  const cancel = () => {
    const wasRecrop = Boolean(sourceMediaRef.current?.path);
    setSrc(null);
    setSrcPrev(null);
    setSelectedFile(null);
    setCropped(false);
    setPreloading(false);
    setSourceMedia(null);
    if (wasRecrop) onRecropClose?.();
  };

  useEffect(() => {
    if (!recrop?.imageUrl || !recrop?.sourcePath) return;

    setSourceMedia({
      path: recrop.sourcePath,
      source: recrop.source,
    });
    setSelectedFile(null);
    setSrcPrev(null);
    setCropped(false);
    setSrc(getCropperImageUrl(recrop.sourcePath) || recrop.imageUrl);
  }, [recrop?.imageUrl, recrop?.sourcePath, recrop?.source]);

  const save = async (e, options = {}) => {
    e?.preventDefault();

    if (!cropped || !srcPrev) {
      showAlert({ msgTitle: "שגיאה", msgText: "חסר חיתוך", msgType: "error" });
      return;
    }

    setPreloading(true);

    const savedSource = sourceMediaRef.current?.path
      ? resolveCroppedImageSource(sourceMediaRef.current)
      : ORIGINAL_IMAGE_SOURCE;

    try {
      const response = await fetch(srcPrev);
      const blob = await response.blob();
      const fileName = selectedFile?.name || `cropped_${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: blob.type });

      const { data } = await getPresignedUrl(file, undefined, itemId);
      if (!data?.uploadUrl) throw new Error("No uploadUrl");

      const uploadRes = await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadRes.ok) throw new Error("S3 upload failed");

      const uploadedPath =
        data.key ||
        data.fileName ||
        uploadRes.url?.split("/").pop()?.split("?")[0] ||
        file.name;

      const img = new Image();
      img.onload = async () => {
        try {
          const media = {
            path: uploadedPath,
            size: file.size,
            width: img.width,
            height: img.height,
            default: Boolean(options?.isDefault),
            source: savedSource,
          };

          const setAsDefault = Boolean(options?.isDefault);

          if (usePgwProductImage) {
            await uploadImg?.({
              newMedia: media,
              setAsDefault,
            });
          } else {
            const res = await UpdateProduct({ productId: itemId, media });
            const addedMedia = res.data.addedImages.map((name, i) => ({
              path: name,
              order: i,
              default: setAsDefault && i === 0,
              size: media.size,
              width: media.width,
              height: media.height,
              source: media.source,
            }));
            await uploadImg?.({ media: addedMedia, setAsDefault });
          }

          cancel();
        } catch (err) {
          console.error(err);
          showAlert({
            msgTitle: "שגיאה",
            msgText: getUploadErrorMessage(err),
            msgType: "error",
          });
          cancel();
        }
      };
      img.onerror = () => {
        showAlert({
          msgTitle: "שגיאה",
          msgText: "לא ניתן לקרוא את התמונה",
          msgType: "error",
        });
        cancel();
      };
      img.src = srcPrev;
    } catch (err) {
      console.error(err);
      showAlert({
        msgTitle: "שגיאה",
        msgText: getUploadErrorMessage(err),
        msgType: "error",
      });
      cancel();
    }
  };

  const _onChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 20_000_000) {
      showAlert({
        msgTitle: "קובץ כבד",
        msgText: "מקסימום 20MB",
        msgType: "info",
      });
      return;
    }

    setSelectedFile(file);
    setSourceMedia(null);

    const reader = new FileReader();
    reader.onload = () => {
      setSrc(reader.result);
      setSrcPrev(null);
      setCropped(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="load-image-wrapper load-image-wrapper--trigger">
      <label className="image-upload-btn">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={_onChange}
          className="image-upload-btn__input"
        />
        <span className="image-upload-btn__icon" aria-hidden>
          <ImagePlus size={22} strokeWidth={2} />
        </span>
        <span className="image-upload-btn__content">
          <span className="image-upload-btn__title">הוסף תמונה</span>
          <span className="image-upload-btn__hint">JPG, PNG · עד 20MB</span>
        </span>
      </label>
      <MyCropperComp
        save={save}
        cancel={cancel}
        src={src}
        srcPrev={srcPrev}
        setSrcPrev={setSrcPrev}
        cropped={cropped}
        setCropped={setCropped}
        preloading={preloading}
      />
    </div>
  );
};

export default MyCropper;
